import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PasteListenerService } from './paste-listener.service';
import { ToastService } from '../../../services/toast.service';
import { AppConstants } from '../../../utilities/constant';

export interface ImageUploadEvent {
  files: File[];
  source: 'click' | 'drag' | 'paste';
}

@Component({
  selector: 'app-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.scss'],
})
export class ImageUploadComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  /**
   * Accepted file types. If not provided, uses default image types.
   * Example: ".jpg,.png" or "image/jpeg,image/png"
   */
  @Input() accept: string = '';

  /**
   * Placeholder/label text displayed in the upload box.
   * Default: 'drag_and_upload'
   */
  @Input() placeholder: string = 'drag_and_upload';

  /**
   * Width of the upload box. Can be CSS value like '100%', '300px', etc.
   */
  @Input() width: string = '100%';

  /**
   * Height of the upload box.
   */
  @Input() height: string = 'auto';

  /**
   * Allow multiple file selection.
   */
  @Input() multiple: boolean = true;

  /**
   * Maximum number of files to accept at once.
   * Default: 10
   */
  @Input() maxFiles: number = 10;

  /**
   * Maximum file size in bytes.
   * Default: 5 MB (5242880 bytes)
   */
  @Input() maxFileSize: number = 5242880; // 5 MB

  /**
   * Disables all upload interactions (click, drag-drop, paste).
   */
  @Input() disabled: boolean = false;

  /**
   * Optional message shown when upload is disabled and user tries to interact.
   */
  @Input() disabledMessage: string = 'Upload is disabled.';

  /**
   * When true, paste is blocked externally (for example while confirmation popup is open).
   */
  @Input() blockPaste: boolean = false;

  /**
   * Time window (ms) to suppress repeated paste attempts of the same clipboard images.
   */
  @Input() pasteCooldownMs: number = 2500;

  /**
   * Suppress identical clipboard image batches pasted within cooldown window.
   */
  @Input() suppressDuplicateClipboardPaste: boolean = true;

  /**
   * Emits when valid files are selected/dropped/pasted.
   */
  @Output() filesSelected = new EventEmitter<ImageUploadEvent>();

  // Default accepted image types
  private readonly DEFAULT_ACCEPT_TYPES = [
    '.jpg',
    '.jpeg',
    '.gif',
    '.png',
    '.webp',
    '.bmp',
    '.tga',
    'image/jpeg',
    'image/jpg',
    'image/gif',
    'image/png',
    'image/webp',
    'image/bmp',
  ];

  isDragOver = false;
  isProcessing = false;

  private acceptedTypes: string[] = [];
  private pasteFeedbackTimeout: any;
  private fileDialogStateResetTimeout: any;
  private isFileDialogOpen = false;
  private recentlyPastedFiles: Set<string> = new Set();
  private lastPasteBatchSignature: string | null = null;
  private lastPasteHandledAt = 0;
  private pasteLockedUntil = 0;

  constructor(
    private pasteListenerService: PasteListenerService,
    private elementRef: ElementRef,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.initializeAcceptedTypes();
    this.registerGlobalPasteListener();
  }

  ngOnDestroy() {
    this.pasteListenerService.unregisterComponent(this.elementRef.nativeElement);
    if (this.pasteFeedbackTimeout) {
      clearTimeout(this.pasteFeedbackTimeout);
    }
    if (this.fileDialogStateResetTimeout) {
      clearTimeout(this.fileDialogStateResetTimeout);
    }
  }

  /**
   * Initialize accepted file types based on input or use defaults.
   */
  private initializeAcceptedTypes() {
    if (this.accept && this.accept.trim().length > 0) {
      // Parse custom accept value
      this.acceptedTypes = this.accept
        .split(',')
        .map((type) => type.trim())
        .filter((type) => type.length > 0);
    } else {
      // Use defaults
      this.acceptedTypes = [...this.DEFAULT_ACCEPT_TYPES];
    }
  }

  /**
   * Register this component with the global paste listener service.
   */
  private registerGlobalPasteListener() {
    this.pasteListenerService.registerComponent(
      this.elementRef.nativeElement,
      (files: File[]) => this.handlePastedFiles(files),
    );
  }

  /**
   * Handle drag over event - show visual feedback.
   */
  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (this.disabled) {
      this.isDragOver = false;
      return;
    }

    this.isDragOver = true;
  }

  /**
   * Handle drag leave event.
   */
  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  /**
   * Handle drop event.
   */
  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (this.disabled) {
      this.showDisabledToast();
      return;
    }

    // Do not process drop while native picker flow is active.
    // Browsers do not allow force-closing OS file dialogs from JS.
    if (this.isFileDialogOpen) {
      this.toastService.presentToast(
        'Please close the file picker before drag-and-drop.',
        3000,
        AppConstants.ERROR_SNACKBAR,
      );
      this.closeNativeFileDialogFlow();
      return;
    }

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFiles(Array.from(files), 'drag');
    }
  }

  /**
   * Trigger file input click.
   */
  triggerFileInput() {
    if (this.disabled) {
      this.showDisabledToast();
      return;
    }

    if (this.fileInput) {
      this.isFileDialogOpen = true;
      this.fileInput.nativeElement.click();
    }
  }

  /**
   * Handle file input change event.
   */
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (files && files.length > 0) {
      this.processFiles(Array.from(files), 'click');
    }

    // Reset input value so same file can be selected again
    input.value = '';
    this.clearFileDialogState();
  }

  /**
   * Handle native file dialog cancel event (when supported by browser).
   */
  onFileDialogCancel() {
    this.clearFileDialogState();
  }

  /**
   * Browsers usually return focus to the window after native picker closes.
   * Use that signal to clear picker state when user cancels the dialog.
   */
  @HostListener('window:focus')
  onWindowFocus() {
    if (!this.isFileDialogOpen) return;

    if (this.fileDialogStateResetTimeout) {
      clearTimeout(this.fileDialogStateResetTimeout);
    }

    this.fileDialogStateResetTimeout = setTimeout(() => {
      this.clearFileDialogState();
    }, 200);
  }

  /**
   * Try to terminate the native picker interaction when a drop happens.
   */
  private closeNativeFileDialogFlow() {
    const inputEl = this.fileInput?.nativeElement;
    if (inputEl) {
      inputEl.blur();
      inputEl.value = '';
    }
  }

  /**
   * Clears internal state related to native file picker lifecycle.
   */
  private clearFileDialogState() {
    if (this.fileDialogStateResetTimeout) {
      clearTimeout(this.fileDialogStateResetTimeout);
      this.fileDialogStateResetTimeout = null;
    }
    this.isFileDialogOpen = false;
  }

  /**
   * Handle pasted files from global paste listener.
   */
  private handlePastedFiles(files: File[]) {
    if (this.disabled) {
      this.showDisabledToast();
      return;
    }

    if (
      this.blockPaste ||
      this.isPasteTemporarilyLocked() ||
      this.isBlockingOverlayOpen()
    ) {
      this.toastService.presentToast(
        'Please complete the current upload action before pasting again.',
        2500,
        AppConstants.WARNING_SNACKBAR,
      );
      return;
    }

    if (this.shouldSuppressDuplicatePaste(files)) {
      return;
    }

    const validFileCount = this.processFiles(files, 'paste');
    if (validFileCount > 0) {
      this.lockPasteTemporarily();
      this.recordPasteBatch(files);
    }

    this.showPasteFeedback();
  }

  /**
   * Shows the configured disabled message in a toast.
   */
  private showDisabledToast() {
    this.toastService.presentToast(
      this.disabledMessage,
      2500,
      AppConstants.WARNING_SNACKBAR,
    );
  }

  /**
   * Process files with validation.
   */
  private processFiles(files: File[], source: 'click' | 'drag' | 'paste'): number {
    if (!files || files.length === 0) return 0;

    // Reject the whole batch before emitting anything when the user exceeds the allowed count.
    // This prevents the native file picker from keeping extra items selected and avoids
    // showing the toast while still allowing invalid files through.
    if (this.maxFiles > 0 && files.length > this.maxFiles) {
      this.toastService.presentToast(
        this.translateService.instant('image_upload_max_files_exceeded', {
          maxFiles: this.maxFiles,
          selectedFiles: files.length,
        }),
        5000,
        AppConstants.ERROR_SNACKBAR,
      );
      return 0;
    }

    this.isProcessing = true;
    const validFiles: File[] = [];
    const oversizedFiles: string[] = [];
    const invalidTypeFiles: string[] = [];
    const duplicateFiles: string[] = [];

    for (const file of files) {
      // File type validation
      if (!this.isAcceptedFileType(file)) {
        invalidTypeFiles.push(file.name);
        continue;
      }

      // File size validation
      if (file.size > this.maxFileSize) {
        oversizedFiles.push(file.name);
        continue;
      }

      // Duplicate detection using file properties
      const fileSignature = `${file.name}|${file.size}|${file.lastModified}`;
      if (this.recentlyPastedFiles.has(fileSignature)) {
        duplicateFiles.push(file.name);
        continue;
      }

      validFiles.push(file);
      this.recentlyPastedFiles.add(fileSignature);
    }

    this.isProcessing = false;

    // Show grouped validation toasts
    if (oversizedFiles.length > 0) {
      const maxSizeMB = (this.maxFileSize / (1024 * 1024)).toFixed(1);
      this.showValidationError(
        oversizedFiles,
        this.translateService.instant('image_upload_file_size_exceeds_limit', {
          maxSizeMB,
        }),
      );
    }
    if (invalidTypeFiles.length > 0) {
      this.showValidationError(
        invalidTypeFiles,
        this.translateService.instant('image_upload_invalid_file_type', {
          acceptedTypes: this.getAcceptedTypesDisplay(),
        }),
      );
    }
    if (duplicateFiles.length > 0) {
      this.showValidationError(
        duplicateFiles,
        this.translateService.instant('image_upload_duplicate_file_detected'),
      );
    }

    // Emit valid files
    if (validFiles.length > 0) {
      this.filesSelected.emit({
        files: validFiles,
        source: source,
      });

      // Clear recently pasted files after successful emission
      setTimeout(() => {
        this.recentlyPastedFiles.clear();
      }, 1000);
    }

    return validFiles.length;
  }

  private isPasteTemporarilyLocked(): boolean {
    return Date.now() < this.pasteLockedUntil;
  }

  private lockPasteTemporarily() {
    if (this.pasteCooldownMs <= 0) return;
    this.pasteLockedUntil = Date.now() + this.pasteCooldownMs;
  }

  private recordPasteBatch(files: File[]) {
    this.lastPasteBatchSignature = this.getClipboardBatchSignature(files);
    this.lastPasteHandledAt = Date.now();
  }

  private shouldSuppressDuplicatePaste(files: File[]): boolean {
    if (!this.suppressDuplicateClipboardPaste || this.pasteCooldownMs <= 0) {
      return false;
    }

    const now = Date.now();
    const signature = this.getClipboardBatchSignature(files);
    return (
      this.lastPasteBatchSignature === signature &&
      now - this.lastPasteHandledAt <= this.pasteCooldownMs
    );
  }

  /**
   * Prevent paste while a blocking UI overlay is currently visible.
   * Covers Ionic overlays and common web modal patterns.
   * Excludes overlays that contain this component itself (e.g. when the
   * upload box lives inside its own ion-modal) so pasting isn't blocked
   * by the modal it's already inside of — only by overlays stacked on top.
   */
  private isBlockingOverlayOpen(): boolean {
    const overlaySelectors = [
      'ion-modal',
      'ion-alert',
      'ion-popover',
      'ion-action-sheet',
      'ion-loading',
      '.cdk-overlay-container .cdk-overlay-pane',
      '.modal.show',
      '[role="dialog"][aria-modal="true"]',
    ];

    const overlays = document.querySelectorAll(overlaySelectors.join(','));
    for (const overlay of Array.from(overlays)) {
      if (overlay.contains(this.elementRef.nativeElement)) {
        continue;
      }
      if (this.isElementVisible(overlay)) {
        return true;
      }
    }
    return false;
  }

  private isElementVisible(element: Element): boolean {
    const htmlEl = element as HTMLElement;
    const style = window.getComputedStyle(htmlEl);

    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }

    const rect = htmlEl.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  /**
   * Clipboard images may get a new lastModified each paste,
   * so signature intentionally excludes it.
   */
  private getClipboardBatchSignature(files: File[]): string {
    return files
      .map((f) => `${f.name}|${f.size}|${f.type}`)
      .sort()
      .join('||');
  }

  /**
   * Check if file type is in accepted list.
   */
  private isAcceptedFileType(file: File): boolean {
    // Check by extension and MIME type
    for (const acceptType of this.acceptedTypes) {
      if (acceptType.startsWith('.')) {
        // Check extension
        if (file.name.toLowerCase().endsWith(acceptType.toLowerCase())) {
          return true;
        }
      } else {
        // Check MIME type
        if (file.type === acceptType) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Get display string of accepted types.
   */
  private getAcceptedTypesDisplay(): string {
    const types = this.acceptedTypes
      .filter((t) => t.startsWith('.'))
      .map((t) => t.substring(1).toUpperCase());
    return types.join(', ') || 'images';
  }

  /**
   * Show validation error message grouped by error type.
   * Lists file names on separate lines followed by the shared reason.
   */
  private showValidationError(fileNames: string[], reason: string) {
    const message = [...fileNames, reason].join('<br>');
    this.toastService.presentToast(message, 5000, AppConstants.ERROR_SNACKBAR);
  }

  /**
   * Show brief feedback when paste happens.
   */
  private showPasteFeedback() {
    const uploadBox = this.elementRef.nativeElement.querySelector(
      '.upload-box-container',
    );
    if (uploadBox) {
      uploadBox.classList.add('paste-feedback');

      if (this.pasteFeedbackTimeout) {
        clearTimeout(this.pasteFeedbackTimeout);
      }

      this.pasteFeedbackTimeout = setTimeout(() => {
        uploadBox.classList.remove('paste-feedback');
      }, 600);
    }
  }

  /**
   * Get the accept attribute value for the file input.
   */
  getAcceptAttribute(): string {
    return this.accept || this.DEFAULT_ACCEPT_TYPES.join(',');
  }
}
